import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, Plus, Trash2, Loader2 } from "lucide-react";
import api from "@/lib/api";

const TRACKS = [
  { id: "all", label: "All Tracks" },
  { id: "data_cloud", label: "Data Cloud" },
  { id: "sfmc", label: "SFMC" },
  { id: "mc_next", label: "MC Next" },
];

export default function JobAlertsPanel() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ keyword: "", track: "all", location: "" });

  const loadAlerts = () => {
    setLoading(true);
    api.get("/jobs/me/alerts").then((r) => setAlerts(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { loadAlerts(); }, []);

  const createAlert = async () => {
    if (!form.keyword.trim() && !form.location.trim() && form.track === "all") {
      toast.error("Add at least a keyword, location, or specific track for your alert");
      return;
    }
    setCreating(true);
    try {
      await api.post("/jobs/me/alerts", form);
      setForm({ keyword: "", track: "all", location: "" });
      toast.success("Job alert created — we'll email you when a match is posted");
      loadAlerts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create alert");
    } finally {
      setCreating(false);
    }
  };

  const deleteAlert = async (id) => {
    await api.delete(`/jobs/me/alerts/${id}`);
    setAlerts((a) => a.filter((x) => x.id !== id));
    toast.success("Alert deleted");
  };

  return (
    <div className="glass-panel rounded-2xl p-5" data-testid="job-alerts-panel">
      <div className="flex items-center gap-2 mb-3">
        <Bell className="w-4 h-4 text-indigo-400" />
        <p className="text-sm font-semibold text-white">Job Alerts</p>
      </div>
      <p className="text-xs text-slate-400 mb-4">Get an email the moment a matching job is posted. Leave fields blank to widen the match.</p>

      <div className="flex flex-wrap gap-2 mb-4">
        <input
          value={form.keyword} onChange={(e) => setForm((f) => ({ ...f, keyword: e.target.value }))}
          placeholder="Keyword (e.g. Data Cloud)" data-testid="job-alert-keyword-input"
          className="flex-1 min-w-[140px] bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white placeholder:text-slate-500"
        />
        <input
          value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
          placeholder="Location" data-testid="job-alert-location-input"
          className="flex-1 min-w-[120px] bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white placeholder:text-slate-500"
        />
        <select
          value={form.track} onChange={(e) => setForm((f) => ({ ...f, track: e.target.value }))} data-testid="job-alert-track-select"
          className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-slate-200"
        >
          {TRACKS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        <button
          onClick={createAlert} disabled={creating} data-testid="job-alert-create-btn"
          className="flex items-center gap-1.5 bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors"
        >
          {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Create Alert
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 text-indigo-400 animate-spin" /></div>
      ) : alerts.length === 0 ? (
        <p className="text-xs text-slate-500 text-center py-3" data-testid="job-alerts-empty">No job alerts yet.</p>
      ) : (
        <div className="space-y-2">
          {alerts.map((a) => (
            <div key={a.id} className="flex items-center justify-between bg-white/[0.03] rounded-lg px-3 py-2" data-testid={`job-alert-row-${a.id}`}>
              <p className="text-xs text-slate-300">
                {a.keyword && <span className="font-semibold text-white">"{a.keyword}"</span>}
                {a.keyword && (a.track !== "all" || a.location) && " · "}
                {a.track !== "all" && <span>{TRACKS.find((t) => t.id === a.track)?.label}</span>}
                {a.track !== "all" && a.location && " · "}
                {a.location && <span>{a.location}</span>}
                {!a.keyword && a.track === "all" && !a.location && <span>All jobs</span>}
              </p>
              <button onClick={() => deleteAlert(a.id)} data-testid={`job-alert-delete-btn-${a.id}`} className="text-slate-500 hover:text-rose-400">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
